
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import ScreenLoader from '../components/ScreenLoader';
import { useTranslation } from 'react-i18next';

const LoginScreen: React.FC = () => {
    const { t } = useTranslation();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [showPassword, setShowPassword] = useState(false);
    const { session, onboardingCompleted, loading: authLoading, isSyncing, sessionInitialized } = useAuth();
    const navigate = useNavigate();

    React.useEffect(() => {
        // Redirection logic: Once session is active and Auth sync is done, move the user.
        if (session && !authLoading && !isSyncing) {
            console.log("[LoginScreen] Redirecting based on onboarding status:", onboardingCompleted);
            if (onboardingCompleted) {
                navigate('/dashboard', { replace: true });
            } else {
                navigate('/questionnaire', { replace: true });
            }
        }
    }, [session, onboardingCompleted, authLoading, isSyncing, navigate]);

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            const { error } = await supabase.auth.signInWithPassword({
                email,
                password,
            });

            if (error) {
                setError(error.message);
                setLoading(false);
            }
            // Successful login will trigger AuthContext update -> useEffect handles navigation
        } catch (err: any) {
            setError(t('auth.login.error_unexpected') + ": " + err.message);
            setLoading(false);
        }
    };

    // Prevent rendering the form while we are discovering if they are already logged in
    // Also prevent if they ARE logged in, so they don't see the form before the useEffect redirects them.
    if (authLoading || !sessionInitialized || session) {
        return <ScreenLoader />;
    }

    return (
        <div className="min-h-screen bg-background-dark flex flex-col items-center justify-center p-4 font-inter">
            <div className="w-full max-w-md space-y-8">
                <div className="text-center space-y-4">
                    <img src="/icon-192x192.png" alt="Aura Method Logo" className="w-16 h-16 mx-auto rounded-2xl drop-shadow-[0_0_15px_rgba(242,127,13,0.5)]" />
                    <h2 className="text-3xl font-black text-white uppercase tracking-tighter">{t('auth.login.title')}</h2>
                </div>

                <div className="bg-surface-card border border-surface-border p-8 rounded-[2rem] shadow-2xl">
                    <form onSubmit={handleLogin} className="space-y-6">
                        {error && (
                            <div className="p-3 bg-red-500/10 border border-red-500/50 rounded-xl text-red-500 text-xs font-bold">
                                {error}
                            </div>
                        )}

                        <div className="space-y-2">
                            <label className="text-[10px] font-black text-text-muted uppercase tracking-widest">{t('auth.login.email')}</label>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="w-full !bg-surface-dark border !border-surface-border rounded-xl px-4 py-3 text-white focus:!border-primary outline-none"
                                required
                            />
                        </div>

                        <div className="space-y-2">
                            <div className="flex justify-between items-center">
                                <label className="text-[10px] font-black text-text-muted uppercase tracking-widest">{t('auth.login.password')}</label>
                                <Link to="/forgot-password" className="text-[10px] font-bold text-primary hover:text-white transition-colors" tabIndex={-1}>
                                    ¿Olvidaste tu contraseña?
                                </Link>
                            </div>
                            <div className="relative">
                                <input
                                    type={showPassword ? "text" : "password"}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="w-full !bg-surface-dark border !border-surface-border rounded-xl px-4 py-3 text-white focus:!border-primary outline-none pr-12"
                                    required
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-white/40 hover:text-primary transition-colors flex items-center justify-center rounded-lg"
                                    tabIndex={-1}
                                    title={showPassword ? t('auth.login.hide') : t('auth.login.show')}
                                >
                                    <span className="material-symbols-outlined text-[20px]">
                                        {showPassword ? 'visibility_off' : 'visibility'}
                                    </span>
                                </button>
                            </div>
                        </div>

                        <motion.button
                            type="submit"
                            disabled={loading}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            className="w-full relative overflow-hidden bg-neutral-900/90 backdrop-blur-xl text-white font-black uppercase tracking-[0.2em] py-5 rounded-2xl shadow-[0_10px_50px_rgba(242,127,13,0.3)] hover:shadow-[0_20px_60px_rgba(242,127,13,0.5)] transition-all flex items-center justify-center gap-3 text-xs border border-primary/40 group/btn disabled:opacity-50"
                        >
                            {/* Pulsing Aura Border */}
                            <div className="absolute inset-0 rounded-2xl border-2 border-primary/20 animate-pulse-slow pointer-events-none"></div>

                            {/* Shimmer Sweep (Amber/Orange) */}
                            <motion.div
                                animate={{
                                    left: ['-100%', '200%'],
                                }}
                                transition={{
                                    duration: 3,
                                    repeat: Infinity,
                                    repeatDelay: 2,
                                    ease: "easeInOut"
                                }}
                                className="absolute top-0 bottom-0 w-1/3 bg-gradient-to-r from-transparent via-primary/30 to-transparent skew-x-[30deg] pointer-events-none"
                            />

                            <span className="relative z-10 bg-gradient-to-r from-white via-white to-primary/50 bg-clip-text text-transparent">
                                {loading ? t('auth.login.loading') : t('auth.login.submit')}
                            </span>
                        </motion.button>
                    </form>

                    <div className="mt-8 text-center">
                        <Link to="/" className="text-xs text-text-secondary hover:text-white">
                            {t('auth.login.back')}
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LoginScreen;
